import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import { Calendar } from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

export function AnimatedRevenueChart({ delay = 0, data: propData }) {
  const [data, setData] = useState({
    labels: [],
    datasets: [
      {
        label: 'Monthly Revenue',
        data: [],
        backgroundColor: 'rgba(99, 102, 241, 0.6)',
        borderColor: 'rgba(99, 102, 241, 1)',
        borderWidth: 1,
      },
    ],
  });

  const defaultData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [
      {
        label: 'Monthly Revenue',
        data: [12500, 15000, 17800, 16400, 19100, 21500, 24000, 25600, 23400, 22000, 26700, 27800],
        backgroundColor: 'rgba(99, 102, 241, 0.6)',
        borderColor: 'rgba(99, 102, 241, 1)',
        borderWidth: 1,
      },
    ],
  };

  const fullData = propData && propData.datasets && propData.datasets.length > 0 ? propData : defaultData;

  useEffect(() => {
    if (!fullData) return;

    // Animate default or propData
    setData({
      labels: [],
      datasets: fullData.datasets.map(dataset => ({
        ...dataset,
        data: []
      }))
    });

    const animationTimeout = setTimeout(() => {
      setData({
        labels: fullData.labels,
        datasets: fullData.datasets.map(dataset => ({
          ...dataset,
          data: dataset.data.map(() => 0)
        }))
      });

      fullData.labels.forEach((_, index) => {
        setTimeout(() => {
          setData(prevData => {
            const newData = { ...prevData };
            newData.datasets = newData.datasets.map((dataset, datasetIndex) => ({
              ...dataset,
              data: [
                ...prevData.datasets[datasetIndex].data.slice(0, index),
                fullData.datasets[datasetIndex].data[index],
                ...prevData.datasets[datasetIndex].data.slice(index + 1)
              ]
            }));
            return newData;
          });
        }, 100 * index);
      });
    }, delay * 1000);

    return () => clearTimeout(animationTimeout);
  }, [delay, propData]);
  
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          display: true,
          drawBorder: false,
        },
        ticks: {
          callback: (value) => `$${value / 1000}k`,
        },
      },
      x: {
        grid: {
          display: false,
          drawBorder: false,
        },
      },
    },
    animation: {
      duration: 1000,
      easing: 'easeOutQuart'
    }
  };
  
  return (
    <motion.div 
      className="lg:col-span-2 bg-white rounded-lg shadow-sm p-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: delay, ease: "easeOut" }}
    >
      <motion.div 
        className="flex items-center justify-between mb-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, delay: delay + 0.2 }}
      >
        <h2 className="text-lg font-medium">Revenue Overview</h2>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-500">This Year</span>
          <motion.div 
            className="flex items-center space-x-1 bg-gray-100 rounded-md px-2 py-1"
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 400, damping: 10 }}
          >
            <Calendar className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-medium">{new Date().getFullYear()}</span>
          </motion.div>
        </div>
      </motion.div>
      
      <div className="h-64">
        <Bar data={data} options={""} />
      </div>
    </motion.div>
  );
}

export function AnimatedUserGrowthChart({ delay = 0, data: propData }) {
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [
      {
        label: 'New Users',
        data: [],
        fill: false,
        borderColor: 'rgba(99, 102, 241, 1)',
        tension: 0.4,
      },
      {
        label: 'Active Users',
        data: [],
        fill: false,
        borderColor: 'rgba(16, 185, 129, 1)',
        tension: 0.4,
      },
    ],
  });  
  // Use propData if provided, else fallback to default fullData
  const fullData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [
      {
        label: 'New Users',
        data: [450, 550, 620, 590, 730, 810, 880, 950, 920, 1050, 1150, 1250],
        fill: false,
        borderColor: 'rgba(99, 102, 241, 1)',
        tension: 0.4,
      },
      // {
      //   label: 'Active Users',
      //   data: [850, 940, 1050, 1100, 1250, 1380, 1500, 1620, 1700, 1850, 1970, 2100],
      //   fill: false,
      //   borderColor: 'rgba(16, 185, 129, 1)',
      //   tension: 0.4,
      // },
    ],
  };

  useEffect(() => {
    if (!propData) {
      // Animate default data
      setChartData({
        labels: [],
        datasets: fullData.datasets.map(dataset => ({
          ...dataset,
          data: []
        }))
      });
      
      const animationTimeout = setTimeout(() => {
        setChartData({
          labels: fullData.labels,
          datasets: fullData.datasets.map(dataset => ({
            ...dataset,
            data: dataset.data.map(() => 0)
          }))
        });
        
        fullData.labels.forEach((_, index) => {
          setTimeout(() => {
            setChartData(prevData => {
              const newData = {...prevData};
              newData.datasets = newData.datasets.map((dataset, datasetIndex) => ({
                ...dataset,
                data: [
                  ...prevData.datasets[datasetIndex].data.slice(0, index),
                  fullData.datasets[datasetIndex].data[index],
                  ...prevData.datasets[datasetIndex].data.slice(index + 1)
                ]
              }));
              return newData;
            });
          }, 120 * index);
        });
      }, delay * 1000);
      
      return () => clearTimeout(animationTimeout);
    } else {
      // Use propData directly
      setChartData(propData);
    }
  }, [delay, propData]);
  
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          font: {
            size: 12,
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          display: true,
          drawBorder: false,
        },
      },
      x: {
        grid: {
          display: false,
          drawBorder: false,
        },
      },
    },
    animation: {
      duration: 1000,
      easing: 'easeOutQuart'
    }
  };
  
  return (
    <motion.div 
      className="bg-white rounded-lg shadow-sm p-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: delay, ease: "easeOut" }}
    >
      <motion.h2 
        className="text-lg font-medium mb-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, delay: delay + 0.2 }}
      >
        User Growth
      </motion.h2>
      <div className="h-64">
        <Line data={chartData} options={options} />
      </div>
    </motion.div>
  );
}

export function AnimatedUserTypeChart({ delay = 0, data: userTypes }) {
  const chartRef = useRef(null);
  const [rotation, setRotation] = useState(0);
  const [data, setData] = useState({
    labels: ['Free Users', 'Premium Users'],
    datasets: [
      {
        data: [0, 0],
        backgroundColor: [
          'rgba(209, 213, 219, 0.7)',
          'rgba(99, 102, 241, 0.7)',
        ],
        borderColor: [
          'rgba(209, 213, 219, 1)',
          'rgba(99, 102, 241, 1)',
        ],
        borderWidth: 1,
      },
    ],
  });

  // Calculate free users as totalUserCount - subscribedUserCount
  const totalUsers = userTypes?.totalUserCount?.count ? parseFloat(userTypes.totalUserCount.count) : 12.61;
  const subscribedUsers = userTypes?.subscribedUserCount?.count ? parseFloat(userTypes.subscribedUserCount.count) : 30.39;
  const freeUsers = Math.max(0, totalUsers - subscribedUsers);

  // Prepare fullData from userTypes prop or fallback to default
  const fullData = {
    labels: ['Free Users', 'Premium Users'],
    datasets: [
      {
        data: [
          freeUsers,
          subscribedUsers,
        ],
        backgroundColor: [
          'rgba(209, 213, 219, 0.7)',
          'rgba(99, 102, 241, 0.7)',
        ],
        borderColor: [
          'rgba(209, 213, 219, 1)',
          'rgba(99, 102, 241, 1)',
        ],
        borderWidth: 1,
      },
    ],
  };

  useEffect(() => {
    // Animate data loading
    const animationTimeout = setTimeout(() => {
      // Start rotation animation
      setRotation(360);

      // Animate to actual values
      setTimeout(() => {
        setData(fullData);
      }, 600);
    }, delay * 1000);

    return () => clearTimeout(animationTimeout);
  }, [delay, userTypes]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          font: {
            size: 12,
          },
        },
      },
    },
    cutout: '70%',
    animation: {
      animateRotate: true,
      animateScale: true,
    },
  };

  const [freePercent, setFreePercent] = useState(0);
  const [premiumPercent, setPremiumPercent] = useState(0);

  useEffect(() => {
    if (data.datasets[0].data[0] === 0 && data.datasets[0].data[1] === 0) return;

    const total = data.datasets[0].data[0] + data.datasets[0].data[1];
    const freePercentValue = Math.round((data.datasets[0].data[0] / total) * 100);
    const premiumPercentValue = Math.round((data.datasets[0].data[1] / total) * 100);

    setFreePercent(freePercentValue);
    setPremiumPercent(premiumPercentValue);
  }, [data]);

  return (
    <motion.div
      className="bg-white rounded-lg shadow-sm p-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: delay, ease: 'easeOut' }}
    >
      <motion.h2
        className="text-lg font-medium mb-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, delay: delay + 0.2 }}
      >
        User Types
      </motion.h2>

      <div className="h-64 flex items-center justify-center">
        <motion.div
          className="w-48"
          initial={{ rotate: 0 }}
          transition={{ duration: 1, delay: delay + 0.3 }}
        >
          <Doughnut data={data} options={options} ref={chartRef} />
        </motion.div>
      </div>

      <motion.div
        className="flex justify-around mt-4"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: delay + 1 }}
      >
        <div className="text-center">
          <motion.div
            className="text-2xl font-bold"
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: delay + 1.2 }}
          >
            {freePercent}%
          </motion.div>
          <div className="text-sm text-gray-500">Free Users</div>
        </div>
        <div className="text-center">
          <motion.div
            className="text-2xl font-bold"
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: delay + 1.4 }}
          >
            {premiumPercent}%
          </motion.div>
          <div className="text-sm text-gray-500">Premium Users</div>
        </div>
      </motion.div>
    </motion.div>
  );
}
